import { useEffect, useState } from "react";
import { Cookie, X, Shield, BarChart3, Megaphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useLocation } from "react-router-dom";
import {
  acceptAll,
  rejectAll,
  readConsent,
  saveConsent,
  CONSENT_OPEN_PREFS_EVENT,
} from "@/lib/consent";

export default function CookieConsent() {
  const { pathname } = useLocation();
  const privacyPath = pathname.startsWith("/es") ? "/es/privacy" : "/privacy";
  const [showBanner, setShowBanner] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      const existing = readConsent();
      if (!existing) {
        setShowBanner(true);
      } else {
        setAnalytics(existing.analytics);
        setMarketing(existing.marketing);
      }
    }, 4000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handler = () => {
      const existing = readConsent();
      if (existing) {
        setAnalytics(existing.analytics);
        setMarketing(existing.marketing);
      }
      setShowPrefs(true);
    };
    window.addEventListener(CONSENT_OPEN_PREFS_EVENT, handler);
    return () => window.removeEventListener(CONSENT_OPEN_PREFS_EVENT, handler);
  }, []);

  const handleAcceptAll = () => {
    acceptAll();
    setAnalytics(true);
    setMarketing(true);
    setShowBanner(false);
    setShowPrefs(false);
  };

  const handleRejectAll = () => {
    rejectAll();
    setAnalytics(false);
    setMarketing(false);
    setShowBanner(false);
    setShowPrefs(false);
  };

  const handleSavePrefs = () => {
    saveConsent({ analytics, marketing });
    setShowBanner(false);
    setShowPrefs(false);
  };

  if (!showBanner && !showPrefs) return null;

  return (
    <>
      {/* Bottom Banner */}
      {showBanner && !showPrefs && (
        <div
          className="fixed bottom-0 left-0 right-0 z-[70] px-3 pb-3 sm:px-6 sm:pb-6 pointer-events-none"
          role="dialog"
          aria-label="Cookie consent"
        >
          <div className="max-w-5xl mx-auto pointer-events-auto bg-[#07111f] text-white border border-white/10 rounded-sm shadow-2xl p-4 sm:p-6">
            <div className="flex flex-col lg:flex-row lg:items-center gap-3 sm:gap-5">
              <div className="flex items-start gap-3 flex-1">
                <div className="hidden sm:flex flex-shrink-0 w-10 h-10 rounded-sm bg-brand-yellow/15 border border-brand-yellow/30 items-center justify-center">
                  <Cookie className="h-5 w-5 text-brand-yellow" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-sm mb-1">
                    We use cookies
                  </h3>
                  <p className="text-white/75 text-[11px] sm:text-xs leading-relaxed max-w-2xl">
                    We use cookies to improve your experience, measure site traffic,
                    and support our marketing. Necessary cookies are always on. You
                    can accept, reject, or customize your choice. See our{" "}
                    <Link to={privacyPath} className="text-brand-yellow underline underline-offset-2">Privacy Policy</Link>.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 lg:flex lg:flex-shrink-0">
                <button
                  onClick={() => setShowPrefs(true)}
                  className="text-white/75 hover:text-[#f5a623] text-[10px] sm:text-xs uppercase tracking-wider font-semibold px-2 sm:px-4 py-2.5 transition-colors"
                >
                  Manage
                </button>
                <Button
                  onClick={handleRejectAll}
                  className="bg-transparent border border-white/40 text-white hover:bg-white/10 hover:border-white/60 hover:text-white uppercase text-[10px] sm:text-xs tracking-wider font-semibold px-2 sm:px-5"
                >
                  Reject
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  className="bg-[#f5a623] text-[#07111f] hover:bg-[#e39a1f] uppercase text-[10px] sm:text-xs tracking-wider font-bold px-2 sm:px-6"
                >
                  Accept
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Modal */}
      {showPrefs && (
        <div
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4 motion-safe:animate-[fadeIn_0.2s_ease-out]"
          role="dialog"
          aria-modal="true"
          aria-label="Cookie preferences"
        >
          <div
            className="absolute inset-0 bg-foreground/70 backdrop-blur-sm"
            onClick={() => setShowPrefs(false)}
          />
          <div className="relative bg-background border border-border rounded-t-sm sm:rounded-sm shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto motion-safe:animate-[slideInUp_0.3s_ease-out]">
            {/* Header */}
            <div className="flex items-start justify-between p-6 border-b border-border">
              <div>
                <div className="h-1 w-10 bg-brand-yellow mb-3" />
                <h2 className="text-foreground font-bold text-xl leading-tight">
                  Cookie Preferences
                </h2>
                <p className="text-muted-foreground text-xs mt-1">
                  Choose which cookies you accept.
                </p>
              </div>
              <button
                onClick={() => setShowPrefs(false)}
                className="p-1.5 text-muted-foreground hover:text-foreground transition-colors rounded-sm hover:bg-secondary"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Categories */}
            <div className="p-6 space-y-4">
              <CategoryRow
                icon={Shield}
                title="Strictly Necessary"
                description="Required for the site to function. Cannot be disabled (session, security, language preference)."
                checked={true}
                disabled={true}
                onChange={() => {}}
              />
              <CategoryRow
                icon={BarChart3}
                title="Analytics"
                description="Help us understand how visitors use the site so we can improve it (Google Analytics 4)."
                checked={analytics}
                onChange={setAnalytics}
              />
              <CategoryRow
                icon={Megaphone}
                title="Marketing"
                description="Allow us to measure the effectiveness of our ads and personalize what you see (Google Ads, Meta Pixel)."
                checked={marketing}
                onChange={setMarketing}
              />
            </div>

            {/* Actions */}
            <div className="p-6 border-t border-border bg-secondary/40 flex flex-col sm:flex-row gap-2 sm:justify-end">
              <Button
                onClick={handleRejectAll}
                variant="outline"
                className="uppercase text-xs tracking-wider font-semibold order-3 sm:order-1"
              >
                Reject All
              </Button>
              <Button
                onClick={handleSavePrefs}
                variant="outline"
                className="uppercase text-xs tracking-wider font-semibold order-2 border-foreground/20"
              >
                Save Choices
              </Button>
              <Button
                onClick={handleAcceptAll}
                className="bg-[#f5a623] text-[#07111f] hover:bg-[#e39a1f] uppercase text-xs tracking-wider font-bold order-1 sm:order-3"
              >
                Accept All
              </Button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideInUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </>
  );
}

function CategoryRow({
  icon: Icon,
  title,
  description,
  checked,
  disabled = false,
  onChange,
}: {
  icon: typeof Shield;
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-4 p-4 border border-border rounded-sm hover:border-brand-yellow/40 transition-colors">
      <div
        className={`flex-shrink-0 w-9 h-9 rounded-sm flex items-center justify-center ${
          disabled ? "bg-muted" : "bg-brand-yellow/10 border border-brand-yellow/20"
        }`}
      >
        <Icon
          className={`h-4 w-4 ${disabled ? "text-muted-foreground" : "text-brand-yellow"}`}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <h3 className="text-foreground font-semibold text-sm">{title}</h3>
          <Toggle checked={checked} disabled={disabled} onChange={onChange} />
        </div>
        <p className="text-muted-foreground text-xs leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

function Toggle({
  checked,
  disabled = false,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${
        disabled
          ? "bg-muted cursor-not-allowed opacity-60"
          : checked
          ? "bg-brand-yellow"
          : "bg-muted-foreground/30"
      }`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-[18px]" : "translate-x-[2px]"
        }`}
      />
    </button>
  );
}
